import { useCallback, useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AssignmentFieldNames } from 'src/model/assignment'
import {
  courseSettingPatch,
  courseSettingCreate,
} from 'src/reducers/course/action'

export function useCourse() {
  return useSelector(state => state.course.course)
}

export function useCourseSetting(key) {
  const course = useCourse()
  const settings = course?.[AssignmentFieldNames.SETTING_DTOS] || []
  const setting = settings.find(x => x.key === key)

  return {
    loading: !course,
    setting,
  }
}

export function useExternalCourseSettings(entity, key) {
  const settings = entity?.[AssignmentFieldNames.SETTING_DTOS] || []

  if (!key) return { loading: !entity, settings }

  return {
    loading: !entity,
    setting: settings.find(x => x.key === key),
  }
}

export function useCourseSettings(key) {
  const dispatch = useDispatch()
  const course = useCourse()
  const { setting: initialSetting } = useCourseSetting(key)
  const [setting, setSetting] = useState(initialSetting)

  // Sync setting state with initialSetting when it changes
  useEffect(() => {
    setSetting(initialSetting)
  }, [initialSetting])

  const execute = useCallback(
    (value = {}, callback = false) => {
      if (!course) return

      dispatch(
        courseSettingCreate(
          {
            courseId: course[AssignmentFieldNames.ID],
            key: key,
            value: JSON.stringify(value),
          },
          course[AssignmentFieldNames.ID],
          dto => {
            if (callback) {
              callback()
            }
            setSetting(dto)
          },
        ),
      )
    },
    [course, dispatch, key],
  )

  const update = useCallback(
    (value, callback) => {
      if (!course || !setting) return

      dispatch(
        courseSettingPatch(
          {
            path: 'Value',
            value: JSON.stringify(value),
          },
          setting.id,
          course[AssignmentFieldNames.ID],
          key,
          () => {
            if (callback) callback()

            setSetting({
              ...setting,
              value: JSON.stringify(value),
            })
          },
        ),
      )
    },
    [course, setting, dispatch, key],
  )

  const exists = Boolean(setting)
  const rawValue = exists && setting.value ? setting.value : null
  const value = useMemo(() => {
    try {
      return rawValue ? JSON.parse(rawValue) : null
    } catch (e) {
      console.error('Error parsing setting value:', e)
      return null
    }
  }, [rawValue])

  return {
    loading: !course,
    execute,
    update,
    exists,
    value,
  }
}

export function patchSetting(dispatch, courseId, id, key, value, callback) {
  dispatch(
    courseSettingPatch(
      {
        path: 'Value',
        value: value,
      },
      id,
      courseId,
      key,
      () => {
        if (callback) {
          callback()
        }
      },
    ),
  )
}

export function createSetting(dispatch, courseId, key, value, callback) {
  dispatch(
    courseSettingCreate(
      {
        courseId: courseId,
        key: key,
        value: value,
      },
      courseId,
      () => {
        if (callback) {
          callback()
        }
      },
    ),
  )
}
